import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CachedUsersService } from '@iot-platform/core';
import { InfoDisplayPipe, UserNamePipe } from '@iot-platform/pipes';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { get, isEmpty } from 'lodash';
import { CustomCellParams } from '../../../models/custom-cell.params';

type CellParams = CustomCellParams & { isFilterRenderer?: boolean };

@Component({
  selector: 'grid-engine-user-cell',
  templateUrl: './user-cell.component.html',
  imports: [TranslatePipe, NgClass, InfoDisplayPipe],
  providers: [UserNamePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-cell.component.scss']
})
export class UserCellComponent implements ICellRendererAngularComp {
  params: WritableSignal<CellParams> = signal(null);
  protected readonly cachedUsersService: CachedUsersService = inject(CachedUsersService);
  addItalicClass: Signal<boolean> = computed(() => {
    const params = this.params();
    return (!params?.value || params.value === this.cachedUsersService.systemUuid) && !!params?.cellOptions?.defaultValue;
  });
  protected readonly translateService: TranslateService = inject(TranslateService);
  protected readonly userNamePipe: UserNamePipe = inject(UserNamePipe);
  value: Signal<string> = computed(() => {
    const params = this.params();
    this.cachedUsersService.cachedUsers();
    if (params) {
      if (isEmpty(params.value) || params.value === this.cachedUsersService.systemUuid) {
        return params.isFilterRenderer
          ? get(params, ['colDef', 'cellRendererParams', 'cellOptions', 'defaultValue'], 'AG_GRID.blanks')
          : get(params, ['cellOptions', 'defaultValue'], params.value);
      } else if (params.value === this.translateService.instant('AG_GRID.selectAll')) {
        return params.value;
      }
      return this.userNamePipe.transform(params.value);
    }
    return null;
  });

  constructor() {
    effect(() => {
      const cachedUsers = this.cachedUsersService.cachedUsers();
      this.params.update((params) => {
        if (params?.colDef?.filterParams) {
          params.colDef.filterParams.users = cachedUsers;
        }
        return { ...params };
      });
    });
  }

  agInit(params: CellParams): void {
    if (params?.colDef?.filterParams) {
      params.colDef.filterParams.users = this.cachedUsersService.cachedUsers();
    }
    this.params.set(params);
  }

  refresh(): boolean {
    return false;
  }
}
